import { useState, type ReactNode } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, LogOut, Pill, ScanLine, Stethoscope, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  getPortalSession,
  listExternalSlips,
  logoutPortal,
  meAppointments,
  meHistory,
  mePrescriptions,
  meProfile,
  relativeLabel,
  type ExternalSlip,
  type MeAppointment,
  type MeHistoryEntry,
  type MePrescription,
  type MeProfile,
} from "./portalApi";
import { ExternalSlipsTab } from "./PortalSlipPage";

/**
 * PUBLIC patient dashboard. Reachable at /portal/me — but if there is no portal
 * session (or it expired) we redirect to /portal. All data is read via the
 * portal me-getters, which send BOTH the portal bearer AND X-Addy-Tenant-Id.
 */

type Tab = "profile" | "appointments" | "prescriptions" | "history" | "slips";

type BadgeVariant = "default" | "success" | "warning" | "muted";

const GENDER_LABEL: Record<string, string> = {
  M: "Nam",
  MALE: "Nam",
  F: "Nữ",
  FEMALE: "Nữ",
  OTHER: "Khác",
};

function genderLabel(g: string | null | undefined): string {
  if (!g) return "—";
  return GENDER_LABEL[g.toUpperCase()] ?? g;
}

function fmtDate(v: string | null | undefined): string {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleDateString("vi-VN");
}

function fmtDateTime(v: string | null | undefined): string {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const APPT_STATUS: Record<string, { label: string; cls: BadgeVariant }> = {
  BOOKED: { label: "Đã đặt", cls: "default" },
  CONFIRMED: { label: "Đã xác nhận", cls: "default" },
  CHECKED_IN: { label: "Đã đến", cls: "success" },
  IN_PROGRESS: { label: "Đang thực hiện", cls: "warning" },
  COMPLETED: { label: "Hoàn thành", cls: "success" },
  DONE: { label: "Hoàn thành", cls: "success" },
  CANCELLED: { label: "Đã hủy", cls: "muted" },
  NO_SHOW: { label: "Không đến", cls: "muted" },
};

const RX_STATUS: Record<string, { label: string; cls: BadgeVariant }> = {
  SIGNED: { label: "Đã ký", cls: "success" },
  ISSUED: { label: "Đã cấp", cls: "success" },
  DRAFT: { label: "Nháp", cls: "muted" },
  CANCELLED: { label: "Đã hủy", cls: "muted" },
};

function statusBadge(
  status: string | null | undefined,
  map: Record<string, { label: string; cls: BadgeVariant }>
) {
  if (!status) return null;
  const m = map[status.toUpperCase()];
  return <Badge variant={m?.cls ?? "default"}>{m?.label ?? status}</Badge>;
}

const TABS: Array<{ id: Tab; label: string; icon: typeof User }> = [
  { id: "profile", label: "Hồ sơ", icon: User },
  { id: "appointments", label: "Lịch hẹn", icon: CalendarClock },
  { id: "prescriptions", label: "Đơn thuốc", icon: Pill },
  { id: "history", label: "Lịch sử khám", icon: Stethoscope },
  { id: "slips", label: "Phiếu khám ngoài", icon: ScanLine },
];

export function PortalDashboardPage() {
  const navigate = useNavigate();
  const session = getPortalSession();
  const [tab, setTab] = useState<Tab>("profile");

  // Guard: no valid portal session -> back to login.
  if (!session) return <Navigate to="/portal" replace />;

  async function logout() {
    await logoutPortal();
    navigate("/portal", { replace: true });
  }

  return (
    <div className="min-h-screen bg-muted/30 px-4 py-6 font-sans text-foreground sm:py-8">
      <div className="mx-auto max-w-3xl">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-base font-bold text-primary">
              {session.clinicName || "Phòng khám"}
            </div>
            <div className="text-sm text-muted-foreground">
              Xin chào, <strong className="text-foreground">{session.patientName || "bạn"}</strong>
            </div>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={() => void logout()}>
            <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
            Đăng xuất
          </Button>
        </div>

        <div className="mb-4 flex flex-wrap gap-2" role="tablist">
          {TABS.map(({ id, label, icon: Icon }) => (
            <Button
              key={id}
              type="button"
              variant={tab === id ? "default" : "outline"}
              size="sm"
              className="rounded-full"
              onClick={() => setTab(id)}
              role="tab"
              aria-selected={tab === id}
            >
              <Icon className="mr-2 h-4 w-4" aria-hidden="true" />
              {label}
            </Button>
          ))}
        </div>

        {tab === "profile" && <ProfileTab />}
        {tab === "appointments" && <AppointmentsTab />}
        {tab === "prescriptions" && <PrescriptionsTab />}
        {tab === "history" && <HistoryTab />}
        {tab === "slips" && <ExternalSlipsTab />}
      </div>
    </div>
  );
}

function Loading() {
  return (
    <Card>
      <CardContent className="py-10 text-center text-sm text-muted-foreground">
        Đang tải…
      </CardContent>
    </Card>
  );
}

function ErrorBox({ error }: { error: unknown }) {
  const msg = error instanceof Error ? error.message : "Đã xảy ra lỗi.";
  return (
    <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
      {msg}
    </div>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return (
    <div className="py-10 text-center text-sm text-muted-foreground">{children}</div>
  );
}

// --------------------------------------------------------------------------

function ProfileTab() {
  const q = useQuery<MeProfile>({ queryKey: ["portal", "me", "profile"], queryFn: meProfile });

  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const p = q.data;
  if (!p) return null;

  const rows: Array<[string, string]> = [
    ["Họ và tên", p.fullName ?? "—"],
    ["Số điện thoại", p.phone ?? "—"],
    ["Ngày sinh", fmtDate(p.dateOfBirth)],
    ["Giới tính", genderLabel(p.gender)],
    ["Email", p.email ?? "—"],
    ["Số thẻ BHYT", p.bhytCardNo ?? "—"],
    ["Địa chỉ", p.addressLine ?? "—"],
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Hồ sơ của tôi</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
          {rows.map(([k, v]) => (
            <div className="min-w-0" key={k}>
              <div className="text-xs text-muted-foreground">{k}</div>
              <div className="break-words text-[15px] font-semibold">{v}</div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function AppointmentsTab() {
  const q = useQuery<MeAppointment[]>({
    queryKey: ["portal", "me", "appointments"],
    queryFn: meAppointments,
  });
  // Lượt khám ghi nhận từ phiếu khám ngoài (như salepro: "Xem chi tiết kết quả khám" trên lịch).
  const slips = useQuery<ExternalSlip[]>({
    queryKey: ["portal", "me", "external-slips"],
    queryFn: listExternalSlips,
    retry: false,
  });

  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const items = q.data ?? [];
  const slipOfVisit = new Map<string, ExternalSlip>();
  for (const s of slips.data ?? []) {
    if (s.visitAppointmentId) slipOfVisit.set(s.visitAppointmentId, s);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Lịch hẹn</CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <Empty>Bạn chưa có lịch hẹn nào.</Empty>
        ) : (
          <div className="flex flex-col gap-2.5">
            {items.map((a) => (
              <div className="rounded-lg border p-3.5" key={a.id}>
                <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2.5">
                  <span className="text-[15px] font-bold">{a.serviceName ?? "Khám bệnh"}</span>
                  {statusBadge(a.status, APPT_STATUS)}
                </div>
                <div className="text-sm text-muted-foreground">
                  {fmtDateTime(a.startAt)}
                  {a.doctorName ? ` · BS. ${a.doctorName}` : ""}
                </div>
                {relativeLabel(a) && (
                  <div className="mt-1 text-sm">Người khám: {relativeLabel(a)}</div>
                )}
                {a.notes && <div className="mt-1.5 text-sm text-muted-foreground">{a.notes}</div>}
                {slipOfVisit.has(a.id) && (
                  <div className="mt-2">
                    <Button asChild variant="outline" size="sm">
                      <Link
                        to={`/portal/slip?id=${encodeURIComponent(slipOfVisit.get(a.id)!.id)}`}
                      >
                        Xem kết quả khám
                      </Link>
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PrescriptionsTab() {
  const q = useQuery<MePrescription[]>({
    queryKey: ["portal", "me", "prescriptions"],
    queryFn: mePrescriptions,
  });

  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const items = q.data ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Đơn thuốc</CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <Empty>Bạn chưa có đơn thuốc nào.</Empty>
        ) : (
          <div className="flex flex-col gap-2.5">
            {items.map((rx) => (
              <div className="rounded-lg border p-3.5" key={rx.id}>
                <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2.5">
                  <span className="text-[15px] font-bold">
                    Đơn thuốc ngày {fmtDate(rx.issuedAt)}
                  </span>
                  {statusBadge(rx.status, RX_STATUS)}
                </div>
                <div className="text-sm text-muted-foreground">
                  {rx.doctorName ? `BS. ${rx.doctorName}` : "—"}
                  {rx.itemCount != null ? ` · ${rx.itemCount} loại thuốc` : ""}
                </div>
                {relativeLabel(rx) && (
                  <div className="mt-1 text-sm">Người khám: {relativeLabel(rx)}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function HistoryTab() {
  const q = useQuery<MeHistoryEntry[]>({
    queryKey: ["portal", "me", "history"],
    queryFn: meHistory,
  });

  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const items = q.data ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Lịch sử khám</CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <Empty>Chưa có lượt khám nào được ghi nhận.</Empty>
        ) : (
          <div className="flex flex-col gap-2.5">
            {items.map((h) => (
              <div className="rounded-lg border p-3.5" key={h.id}>
                <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2.5">
                  <span className="text-[15px] font-bold">
                    {h.diagnosis || h.serviceName || h.chiefComplaint || "Lượt khám"}
                  </span>
                  {statusBadge(h.status, APPT_STATUS)}
                </div>
                <div className="text-sm text-muted-foreground">
                  {fmtDateTime(h.examDate ?? h.startAt)}
                  {h.doctorName ? ` · BS. ${h.doctorName}` : ""}
                </div>
                {relativeLabel(h) && (
                  <div className="mt-1 text-sm">Người khám: {relativeLabel(h)}</div>
                )}
                {h.chiefComplaint && h.chiefComplaint !== (h.diagnosis || h.serviceName) && (
                  <div className="mt-1.5 text-sm text-muted-foreground">
                    Lý do khám: {h.chiefComplaint}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
